import React from 'react';
import classNames from 'classnames';

import {
  Accordion, AccordionItem, Container, WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { DynamicMediaItem } from '@uncinc/react-drupal-core';

import type { ParagraphFAQ as Type } from '../../../types/uncinc_profile/paragraphs';
import { ButtonWrapper } from '../../../components';

import './style.scss';

interface ParagraphFAQProps {
  id?: string;
  entity: ImmutableEntity<Type>;
  isWide?: boolean;
  noBootstrap?: boolean;
}

const ParagraphFAQ: React.FC<ParagraphFAQProps> = ({
  id, entity, isWide, noBootstrap,
}) => {
  const paragraphFAQ = entity.toJS();
  const title = paragraphFAQ.field_title[0]?.value;
  const body = paragraphFAQ.field_body[0]?.processed;
  const faqItems = paragraphFAQ.field_faq;

  const link = paragraphFAQ.field_link?.[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const secondaryLink = paragraphFAQ.field_link_secondary?.[0];
  const secondaryLinkTitle = secondaryLink?.title;
  const secondaryLinkUrl = secondaryLink?.alias || secondaryLink?.uri;

  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': window.location.pathname,
    mainEntity: faqItems.map((faqItem) => ({
      '@type': 'Question',
      name: faqItem.entity.title[0].value,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqItem.entity.body[0].processed,
      },
    })),
  });

  const bodyClass = classNames(
    isWide
      ? 'col-12 col-md-10 col-lg-5 col-xl-4'
      : 'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-1',
  );
  const faqClass = classNames(
    isWide
      ? 'col-12 col-lg-7 col-xl-7 offset-xl-1'
      : 'col-12 col-md-10 offset-md-1',
  );

  const accordionItems = faqItems
    .map((faqItem) => {
      const faqTitle = faqItem.entity.title[0].value;
      const faqKey = faqItem.target_id;
      const faqBody = faqItem.entity.body[0].processed;
      const faqMedia = faqItem.entity.field_media_item?.[0];
      const faqMediaDescription = faqMedia?.entity.field_caption[0]?.value;
      const faqLink = faqItem.entity.field_link?.[0]?.uri;
      const faqLinkTitle = faqItem.entity.field_link?.[0]?.title;

      return (
        <AccordionItem
          target_id={faqKey}
          title={faqTitle}
          className="ParagraphFAQ__accordion__item"
          materialIconName="keyboard_arrow_up"
          iconClassName="toggleIcon"
          content={(
            <>
              <WysiwygField html={faqBody} />
              {(faqLink || faqMedia) && (
                <div className="Accordion__item__wrapper">

                  {faqLink && (
                    <ButtonWrapper
                      type="textual"
                      href={faqLink}
                    >
                      {faqLinkTitle}
                    </ButtonWrapper>
                  )}

                  {faqMedia && (
                    <DynamicMediaItem
                      media={faqMedia}
                      imageAspectRatio="4_3"
                      className="Accordion__item__media"
                      caption={faqMediaDescription}
                    />
                  )}
                </div>
              )}
            </>
          )}
        />
      );
    });

  return (
    <>
      <script type="application/ld+json">
        {schema}
      </script>
      <div id={id} className="Paragraph ParagraphFAQ">
        <Container>
          <div className={noBootstrap ? 'noBootstrapRow' : 'row'}>
            <div className={noBootstrap ? 'noBootstrapColumn' : bodyClass}>
              {(title || body) && (
                <>
                  {title && (
                    <h2 className="ParagraphFAQ__title">{title}</h2>
                  )}
                  {body && (
                    <WysiwygField
                      className="ParagraphFAQ__body"
                      property="schema:text"
                      html={body}
                    />
                  )}
                </>
              )}
              {(linkUrl || secondaryLinkUrl) && (
                <div className="ParagraphFAQ__links">
                  {linkUrl && (
                    <ButtonWrapper
                      type="secondary"
                      href={linkUrl}
                    >
                      {linkTitle}
                    </ButtonWrapper>
                  )}

                  {secondaryLinkUrl && (
                    <ButtonWrapper
                      type="textual"
                      href={secondaryLinkUrl}
                    >
                      {secondaryLinkTitle}
                    </ButtonWrapper>
                  )}
                </div>
              )}
            </div>
            <div className={noBootstrap ? undefined : faqClass}>
              <Accordion className="ParagraphFAQ__accordion">
                {accordionItems}
              </Accordion>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};
export default ParagraphFAQ;
